import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth.jsx';
import { useRealtimeBuscas } from '../../hooks/useRealtime';
import { usePWA } from '../../hooks/usePWA';
import { supabase } from '../../lib/supabase';
import { ROUTES } from '../../lib/constants';
import Button from '../../components/common/Button';
import StatusBadge from '../../components/common/StatusBadge';
import Card from '../../components/common/Card';
import logoImg from '../../assets/logo.png';

export default function ParentApp() {
    const navigate = useNavigate();
    const { user, profile, signOut, isParent } = useAuth();
    const [realtimeEnabled, setRealtimeEnabled] = useState(false);
    const { buscas, loading: loadingBuscas } = useRealtimeBuscas(null, user?.id, realtimeEnabled);
    const { isInstallable, installApp } = usePWA();

    const [alunos, setAlunos] = useState([]);
    const [selectedAlunosIds, setSelectedAlunosIds] = useState([]);
    const [loadingAlunos, setLoadingAlunos] = useState(true);
    const [creatingBusca, setCreatingBusca] = useState(false);
    const [accessCode, setAccessCode] = useState('');
    const [linking, setLinking] = useState(false);
    const [showLinkInput, setShowLinkInput] = useState(false);

    // Novos estados para Menu e Perfil
    const [showMenu, setShowMenu] = useState(false);
    const [showProfileModal, setShowProfileModal] = useState(false);
    const [profileSuccess, setProfileSuccess] = useState(false);
    const [editData, setEditData] = useState({
        nome_completo: '',
        telefone: '',
        placa_veiculo: ''
    });

    // Sincronizar dados do perfil
    useEffect(() => {
        if (profile) {
            setEditData({
                nome_completo: profile.nome_completo || '',
                telefone: profile.telefone || '',
                placa_veiculo: profile.placa_veiculo || ''
            });
        }
    }, [profile]);

    // Redirecionar se não for responsável
    useEffect(() => {
        if (profile && !isParent) {
            navigate(ROUTES.APP_SCHOOL, { replace: true });
        }
    }, [profile, isParent, navigate]);

    // Verificar se existe um código vindo de QR Code (salvo no Register)
    useEffect(() => {
        const pendingCode = sessionStorage.getItem('pending_student_code');
        if (pendingCode && user) {
            console.log('Detectado código pendente do QR Code:', pendingCode);
            // Pequeno delay para garantir que o resto carregou
            setTimeout(() => {
                handleAutoLink(pendingCode);
                sessionStorage.removeItem('pending_student_code');
            }, 500);
        }
    }, [user]);

    const handleAutoLink = async (code) => {
        try {
            setLinking(true);
            const { data: aluno, error: fetchError } = await supabase
                .from('alunos')
                .select('*')
                .eq('codigo_acesso', code.toUpperCase())
                .single();

            if (aluno) {
                const { error: linkError } = await supabase
                    .from('pessoas_autorizadas')
                    .insert([{
                        aluno_id: aluno.id,
                        usuario_id: user.id,
                        parentesco: 'Responsável',
                        pode_buscar: true
                    }]);

                if (!linkError) {
                    await loadAlunos();
                    alert(`Aluno ${aluno.nome_completo} vinculado com sucesso via QR Code!`);
                }
            }
        } catch (e) {
            console.error('Erro no auto-link:', e);
        } finally {
            setLinking(false);
        }
    };

    // Carregar alunos autorizados
    useEffect(() => {
        if (user) {
            loadAlunos();
        }
    }, [user]);

    const loadAlunos = async () => {
        try {
            setLoadingAlunos(true);
            const { data, error } = await supabase
                .from('pessoas_autorizadas')
                .select(`
          *,
          aluno:alunos(*)
        `)
                .eq('usuario_id', user.id)
                .eq('pode_buscar', true);

            if (error) throw error;
            const fetchedAlunos = data?.map((item) => item.aluno) || [];
            setAlunos(fetchedAlunos);

            // Selecionar todos os alunos por padrão na primeira carga
            if (fetchedAlunos.length > 0) {
                setSelectedAlunosIds(fetchedAlunos.map(a => a.id));
            }
        } catch (error) {
            console.error('Erro ao carregar alunos:', error);
        } finally {
            setLoadingAlunos(false);
        }
    };

    // Verificar se existem buscas ativas para ligar o Realtime episodicamente
    useEffect(() => {
        const checkActiveBuscas = async () => {
            if (!user) return;
            const { data, error } = await supabase
                .from('buscas')
                .select('id')
                .eq('responsavel_id', user.id)
                .in('status', ['chegando', 'preparando', 'pronto'])
                .limit(1);

            if (!error && data?.length > 0) {
                setRealtimeEnabled(true);
            }
        };
        checkActiveBuscas();
    }, [user]);

    // Lógica para desligar o Realtime quando não houver mais nada ativo
    useEffect(() => {
        if (realtimeEnabled && buscas.length > 0) {
            const hasActive = buscas.some(b => ['chegando', 'preparando', 'pronto'].includes(b.status));
            if (!hasActive) {
                // Pequeno delay para garantir que o usuário veja o status 'entregue' antes de desconectar
                const timer = setTimeout(() => setRealtimeEnabled(false), 5000);
                return () => clearTimeout(timer);
            }
        }
    }, [buscas, realtimeEnabled]);

    const toggleAlunoSelection = (id) => {
        setSelectedAlunosIds(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    const handleLinkStudent = async (e) => {
        if (e) e.preventDefault();
        if (!accessCode) return;

        try {
            setLinking(true);

            // 1. Buscar o aluno pelo código
            const { data: aluno, error: fetchError } = await supabase
                .from('alunos')
                .select('*')
                .eq('codigo_acesso', accessCode.toUpperCase())
                .single();

            if (fetchError || !aluno) {
                alert('Código inválido ou aluno não encontrado.');
                return;
            }

            // 2. Criar o vínculo
            const { error: linkError } = await supabase
                .from('pessoas_autorizadas')
                .insert([{
                    aluno_id: aluno.id,
                    usuario_id: user.id,
                    parentesco: 'Responsável',
                    pode_buscar: true
                }]);

            if (linkError) {
                if (linkError.code === '23505') {
                    alert('Você já tem vínculo com este aluno.');
                } else {
                    throw linkError;
                }
            } else {
                setAccessCode('');
                setShowLinkInput(false);
                await loadAlunos();
                alert(`Sucesso! Agora você pode buscar ${aluno.nome_completo}.`);
            }

        } catch (error) {
            console.error('Erro ao vincular aluno:', error);
            alert('Erro ao processar o vínculo.');
        } finally {
            setLinking(false);
        }
    };

    const handleUpdateProfile = async (e) => {
        e.preventDefault();
        try {
            setLoadingAlunos(true);
            const { error } = await supabase
                .from('usuarios')
                .update({
                    nome_completo: editData.nome_completo,
                    telefone: editData.telefone,
                    placa_veiculo: editData.placa_veiculo
                })
                .eq('id', user.id);

            if (error) throw error;

            setProfileSuccess(true);
            setTimeout(() => {
                setProfileSuccess(false);
                setShowProfileModal(false);
            }, 2000);

            // Recarregar perfil no estado global sem dar refresh na página
            // window.location.reload(); 
        } catch (error) {
            console.error('Erro ao atualizar perfil:', error);
            alert('Erro ao atualizar perfil.');
        } finally {
            setLoadingAlunos(false);
        }
    };

    const [showAccessModal, setShowAccessModal] = useState(null); // alunoId
    const [authorizedPeople, setAuthorizedPeople] = useState([]);

    const loadAuthorizedPeople = async (alunoId) => {
        try {
            const { data, error } = await supabase
                .from('pessoas_autorizadas')
                .select(`
                    id,
                    usuario:usuarios(id, nome_completo, telefone)
                `)
                .eq('aluno_id', alunoId)
                .neq('usuario_id', user.id); // Não listar a si mesmo

            if (error) throw error;
            setAuthorizedPeople(data || []);
            setShowAccessModal(alunoId);
        } catch (error) {
            console.error('Erro ao carregar autorizados:', error);
        }
    };

    const handleRevokeAccess = async (authorizedId) => {
        if (!confirm('Deseja realmente remover o acesso desta pessoa?')) return;
        try {
            const { error } = await supabase
                .from('pessoas_autorizadas')
                .delete()
                .eq('id', authorizedId);

            if (error) throw error;
            setAuthorizedPeople(prev => prev.filter(p => p.id !== authorizedId));
            alert('Acesso removido com sucesso.');
        } catch (error) {
            console.error('Erro ao remover acesso:', error);
            alert('Você não tem permissão para remover esta pessoa.');
        }
    };

    const handleCancelBusca = async () => {
        if (buscasAtivasDoPai.length === 0) return;
        if (!confirm('Deseja realmente cancelar os chamados ativos?')) return;

        try {
            setCreatingBusca(true);
            const idsParaCancelar = buscasAtivasDoPai.map(b => b.id);

            const { error } = await supabase
                .from('buscas')
                .update({ status: 'cancelado', cancelado_em: new Date().toISOString() })
                .in('id', idsParaCancelar);

            if (error) throw error;
        } catch (error) {
            console.error('Erro ao cancelar busca:', error);
            alert('Não foi possível cancelar o chamado.');
        } finally {
            setCreatingBusca(false);
        }
    };

    const handleCreateBusca = async () => {
        if (selectedAlunosIds.length === 0) return;

        try {
            setCreatingBusca(true);
            setRealtimeEnabled(true);

            // Se for um re-chamado (clicou no botão de Reavisar)
            // Simplesmente atualiza o timestamp do atual
            if (buscasAtivasDoPai.length > 0) {
                const { error } = await supabase
                    .from('buscas')
                    .update({ atualizado_em: new Date().toISOString() })
                    .in('id', buscasAtivasDoPai.map(b => b.id));
                if (error) throw error;
                return;
            }

            const buscasToInsert = selectedAlunosIds.map(id => {
                const aluno = alunos.find(a => a.id === id);
                return {
                    aluno_id: id,
                    responsavel_id: user.id,
                    escola_id: aluno.escola_id,
                    status: 'chegando',
                };
            });

            const { error } = await supabase
                .from('buscas')
                .insert(buscasToInsert);

            if (error) throw error;
        } catch (error) {
            console.error('Erro ao criar busca:', error);
            alert('Erro ao avisar a escola. Verifique sua conexão.');
        } finally {
            setCreatingBusca(false);
        }
    };

    const handleLogout = async () => {
        await signOut();
        navigate(ROUTES.HOME);
    };

    // Verificar buscas ativas
    const buscasAtivasDoPai = buscas.filter(
        (b) => b.status !== 'entregue' && b.status !== 'cancelado'
    );

    if (loadingAlunos) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center">
                <div className="text-center">
                    <div className="text-7xl animate-bounce mb-4">🚗</div>
                    <p className="text-xl text-gray-600 font-medium">Carregando seus alunos...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-green-50 to-white pb-24 relative overflow-x-hidden">
            {/* Overlay do Menu */}
            {showMenu && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 animate-fade-in"
                    onClick={() => setShowMenu(false)}
                />
            )}

            {/* Menu Hamburger Lateral */}
            <aside className={`fixed top-0 right-0 h-full bg-white w-72 z-50 shadow-2xl transition-transform duration-300 transform ${showMenu ? 'translate-x-0' : 'translate-x-full'}`}>
                <div className="p-8 h-full flex flex-col">
                    <div className="flex justify-between items-center mb-10">
                        <h2 className="text-2xl font-black tracking-tighter text-gray-900">Configurações</h2>
                        <button onClick={() => setShowMenu(false)} className="text-gray-400 text-2xl">✕</button>
                    </div>

                    <nav className="flex-1 space-y-2">
                        <button
                            onClick={() => { setShowProfileModal(true); setShowMenu(false); }}
                            className="w-full text-left px-5 py-4 rounded-2xl bg-gray-50 hover:bg-gray-100 transition-colors flex items-center gap-3 font-bold text-gray-700"
                        >
                            <span>👤</span> Editar Perfil
                        </button>
                        <button
                            onClick={() => { setShowLinkInput(true); setShowMenu(false); }}
                            className="w-full text-left px-5 py-4 rounded-2xl bg-gray-50 hover:bg-gray-100 transition-colors flex items-center gap-3 font-bold text-gray-700"
                        >
                            <span>➕</span> Vincular Novo Aluno
                        </button>

                        {isInstallable && (
                            <button
                                onClick={() => { installApp(); setShowMenu(false); }}
                                className="w-full text-left px-5 py-4 rounded-2xl bg-primary text-white shadow-lg shadow-primary/20 hover:bg-primary/90 transition-all flex items-center gap-3 font-black mt-4 animate-pulse-subtle"
                            >
                                <span>📱</span> INSTALAR APP NO CELULAR
                            </button>
                        )}
                    </nav>

                    <button
                        onClick={handleLogout}
                        className="w-full px-5 py-4 rounded-2xl bg-red-50 text-red-600 hover:bg-red-100 transition-colors flex items-center gap-3 font-bold mt-auto"
                    >
                        <span>🚪</span> Sair da Conta
                    </button>
                </div>
            </aside>

            {/* Header */}
            <header className="bg-white shadow-sm border-b sticky top-0 z-20">
                <div className="container mx-auto px-4 py-4">
                    <div className="flex justify-between items-center">
                        <div className="flex items-center gap-3">
                            <img src={logoImg} alt="Fila0 Logo" className="h-10 w-auto object-contain" />
                            <div>
                                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest leading-none">Bem-vindo</p>
                                <p className="text-sm text-gray-800 font-black leading-tight">{profile?.nome_completo?.split(' ')[0]}</p>
                                {profile?.placa_veiculo && (
                                    <p className="text-[9px] font-black text-primary uppercase mt-0.5">🚗 {profile.placa_veiculo}</p>
                                )}
                            </div>
                        </div>

                        <button
                            onClick={() => setShowMenu(true)}
                            className="bg-gray-100 p-2.5 rounded-xl text-gray-800 hover:bg-gray-200 transition-all font-black text-lg"
                        >
                            ☰
                        </button>
                    </div>
                </div>
            </header>

            {/* Modal de Edição de Perfil */}
            {showProfileModal && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/60" onClick={() => setShowProfileModal(false)} />
                    <Card className="relative w-full max-w-md animate-scale-in rounded-3xl overflow-hidden p-0 bg-white">
                        <div className="bg-primary p-6 text-white text-center">
                            <h3 className="text-2xl font-black">Editar Perfil</h3>
                            <p className="text-xs text-white/70 font-bold uppercase tracking-widest mt-1">Mantenha seus dados atualizados</p>
                        </div>
                        <form onSubmit={handleUpdateProfile} className="p-8 space-y-5">
                            <div>
                                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-1">Nome Completo</label>
                                <input
                                    type="text"
                                    value={editData.nome_completo}
                                    onChange={(e) => setEditData(p => ({ ...p, nome_completo: e.target.value }))}
                                    className="w-full px-4 py-3 bg-gray-50 border-2 border-transparent focus:border-primary rounded-2xl font-bold transition-all"
                                    required
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-1">WhatsApp</label>
                                    <input
                                        type="tel"
                                        value={editData.telefone}
                                        onChange={(e) => setEditData(p => ({ ...p, telefone: e.target.value }))}
                                        className="w-full px-4 py-3 bg-gray-50 border-2 border-transparent focus:border-primary rounded-2xl font-bold transition-all"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-1">Placa do Carro</label>
                                    <input
                                        type="text"
                                        value={editData.placa_veiculo}
                                        onChange={(e) => setEditData(p => ({ ...p, placa_veiculo: e.target.value.toUpperCase() }))}
                                        className="w-full px-4 py-3 bg-gray-50 border-2 border-transparent focus:border-primary rounded-2xl font-bold transition-all uppercase"
                                        placeholder="ABC-1234"
                                        maxLength={8}
                                    />
                                </div>
                            </div>
                            {profileSuccess ? (
                                <div className="bg-green-50 border-2 border-green-100 text-green-600 p-4 rounded-2xl text-center animate-bounce mb-4 font-black text-sm uppercase tracking-widest">
                                    ✨ Perfil Atualizado! ✨
                                </div>
                            ) : (
                                <div className="pt-4 flex gap-3">
                                    <Button variant="outline" type="button" onClick={() => setShowProfileModal(false)} className="flex-1 rounded-2xl">Cancelar</Button>
                                    <Button variant="primary" type="submit" className="flex-[2] rounded-2xl py-4 shadow-xl">Salvar Mudanças</Button>
                                </div>
                            )}
                        </form>
                    </Card>
                </div>
            )}

            {/* Main Content */}
            <main className="container mx-auto px-4 py-8 max-w-lg">
                {alunos.length === 0 && !showLinkInput ? (
                    <Card className="text-center py-16 animate-fade-in shadow-xl rounded-3xl">
                        <div className="text-8xl mb-6">👨‍👩‍👧‍👦</div>
                        <h2 className="text-3xl font-black mb-3 text-gray-900 tracking-tighter">Quase lá!</h2>
                        <p className="text-gray-500 mb-10 leading-relaxed font-medium">
                            Vincule seu primeiro aluno usando o código de 6 caracteres fornecido pela escola.
                        </p>
                        <Button
                            variant="primary"
                            className="w-full py-5 text-xl rounded-2xl shadow-lg"
                            onClick={() => setShowLinkInput(true)}
                        >
                            🔗 Vincular Aluno
                        </Button>
                    </Card>
                ) : (alunos.length === 0 || showLinkInput) ? (
                    <Card className="animate-fade-in rounded-3xl shadow-xl border-t-8 border-primary">
                        <div className="flex justify-between items-center mb-8">
                            <h2 className="text-2xl font-black tracking-tighter">Vincular Aluno</h2>
                            {alunos.length > 0 && (
                                <button onClick={() => setShowLinkInput(false)} className="bg-gray-100 p-2 rounded-full text-gray-400">✕</button>
                            )}
                        </div>
                        <form onSubmit={handleLinkStudent}>
                            <p className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-6">Código de Acesso</p>
                            <input
                                type="text"
                                value={accessCode}
                                onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                                placeholder="P7X2H9"
                                className="w-full px-4 py-5 text-4xl font-mono text-center tracking-[0.5em] border-2 border-gray-100 rounded-2xl focus:border-primary focus:ring-0 mb-8 uppercase bg-gray-50 placeholder:opacity-20"
                                maxLength={6}
                                required
                            />
                            <div className="flex gap-4">
                                {alunos.length > 0 && (
                                    <Button variant="outline" type="button" onClick={() => setShowLinkInput(false)} className="flex-1 rounded-2xl">
                                        Cancelar
                                    </Button>
                                )}
                                <Button
                                    variant="primary"
                                    type="submit"
                                    loading={linking}
                                    className="flex-[2] py-5 rounded-2xl text-lg shadow-lg"
                                >
                                    Confirmar
                                </Button>
                            </div>
                        </form>
                    </Card>
                ) : (
                    <div className="space-y-8 animate-fade-in">
                        {/* Lista de Seleção Múltipla */}
                        <div className="flex justify-between items-center px-1">
                            <h2 className="text-xs font-black text-gray-400 uppercase tracking-widest">Toque para selecionar</h2>
                            <button onClick={() => setShowLinkInput(true)} className="text-xs font-bold text-primary underline decoration-2 underline-offset-4">+ Add Novo</button>
                        </div>

                        <div className="grid gap-4 pb-20">
                            {alunos.map((aluno) => {
                                const isSelected = selectedAlunosIds.includes(aluno.id);
                                const ativa = buscasAtivasDoPai.find(b => b.aluno_id === aluno.id);

                                return (
                                    <div key={aluno.id} className="space-y-2">
                                        <button
                                            onClick={() => toggleAlunoSelection(aluno.id)}
                                            className={`w-full text-left transition-all duration-300 rounded-3xl p-5 border-2 flex items-center justify-between shadow-sm ${isSelected
                                                ? 'border-primary bg-primary/5'
                                                : 'border-white bg-white hover:border-gray-200'
                                                } ${ativa ? 'opacity-90' : ''}`}
                                        >
                                            <div className="flex items-center gap-4">
                                                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-3xl transition-colors ${isSelected ? 'bg-primary text-white shadow-lg' : 'bg-gray-100'}`}>
                                                    {isSelected ? '✓' : '👦'}
                                                </div>
                                                <div>
                                                    <p className={`font-black tracking-tighter text-lg ${isSelected ? 'text-primary' : 'text-gray-900'}`}>
                                                        {aluno.nome_completo}
                                                    </p>
                                                    <p className="text-xs font-black text-gray-400 uppercase tracking-widest flex items-center gap-1">
                                                        {aluno.turma}
                                                    </p>
                                                </div>
                                            </div>

                                            {ativa && (
                                                <div className="flex flex-col items-end">
                                                    <StatusBadge status={ativa.status} showIcon={false} />
                                                    <span className="text-[10px] font-black text-primary animate-pulse uppercase mt-1">Ativo</span>
                                                </div>
                                            )}
                                        </button>

                                        <div className="flex gap-2 px-1">
                                            <button
                                                onClick={() => loadAuthorizedPeople(aluno.id)}
                                                className="flex-1 text-[10px] font-black uppercase tracking-widest py-2.5 rounded-xl bg-white text-gray-400 hover:text-primary transition-all border border-gray-100 flex items-center justify-center gap-2"
                                            >
                                                👥 Gestão de Acessos
                                            </button>
                                            <button
                                                onClick={() => handleRemoveStudentLink(aluno.id)}
                                                className="px-4 py-2.5 rounded-xl bg-white text-gray-300 hover:text-red-500 transition-all border border-gray-100"
                                                title="Remover este aluno"
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Botão de Chamar (Sticky-like) */}
                        <div className={`fixed bottom-0 left-0 right-0 p-4 bg-white/80 backdrop-blur-md border-t border-gray-100 transition-all duration-500 transform ${selectedAlunosIds.length > 0 ? 'translate-y-0' : 'translate-y-full'}`}>
                            <div className="container mx-auto max-w-lg">
                                {buscasAtivasDoPai.length > 0 ? (
                                    <div className="flex gap-3">
                                        <Button
                                            variant="danger"
                                            onClick={handleCancelBusca}
                                            loading={creatingBusca}
                                            className="flex-1 py-5 text-lg shadow-xl shadow-red-200 rounded-3xl font-black uppercase tracking-tighter"
                                        >
                                            ❌ Cancelar
                                        </Button>

                                        {/* Só mostra REAVISAR se a escola ainda não começou a preparar (status === 'chegando') */}
                                        {buscasAtivasDoPai.some(b => b.status === 'chegando') && (
                                            <Button
                                                variant="primary"
                                                onClick={handleCreateBusca}
                                                loading={creatingBusca}
                                                className="flex-[2] py-5 text-lg shadow-xl shadow-primary/20 rounded-3xl font-black uppercase tracking-tighter"
                                            >
                                                🔔 REAVISAR
                                            </Button>
                                        )}
                                    </div>
                                ) : (
                                    <Button
                                        variant="success"
                                        onClick={handleCreateBusca}
                                        loading={creatingBusca}
                                        className="w-full py-6 text-2xl shadow-2xl shadow-success/40 rounded-3xl font-black uppercase tracking-tighter transform active:scale-95 flex items-center justify-center gap-3"
                                    >
                                        <span className="text-3xl">🚗</span>
                                        CHAMAR {selectedAlunosIds.length === 1 ? 'ALUNO' : 'ALUNOS'}
                                    </Button>
                                )}
                            </div>
                        </div>

                        {/* Detalhes do Status */}
                        {buscasAtivasDoPai.length > 0 && (
                            <div className="space-y-4 py-8 border-t-2 border-dashed border-gray-100 mt-8">
                                <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest text-center px-1">O que está acontecendo:</h3>
                                <div className="space-y-3">
                                    {buscasAtivasDoPai.map(b => {
                                        const a = alunos.find(x => x.id === b.aluno_id);
                                        return (
                                            <div key={b.id} className="bg-gray-50 rounded-2xl p-4 flex items-center justify-between border border-gray-100">
                                                <div className="flex items-center gap-3">
                                                    <span className="text-2xl">
                                                        {b.status === 'chegando' ? '⏳' : b.status === 'preparando' ? '🎒' : '✅'}
                                                    </span>
                                                    <div>
                                                        <p className="font-bold text-gray-900 leading-none">{a?.nome_completo}</p>
                                                        <p className="text-[10px] text-gray-400 font-bold uppercase mt-1">
                                                            {b.status === 'chegando' ? 'Escola avisada' : b.status === 'preparando' ? 'Descendo' : 'Pode buscar agora'}
                                                        </p>
                                                    </div>
                                                </div>
                                                <StatusBadge status={b.status} />
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </main>

            {/* Modal de Gerenciamento de Acesso */}
            {showAccessModal && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowAccessModal(null)} />
                    <Card className="relative w-full max-w-sm animate-scale-in rounded-3xl overflow-hidden p-0 bg-white shadow-2xl">
                        <div className="bg-gray-900 p-6 text-white text-center">
                            <h3 className="text-xl font-black tracking-tighter">Gestão de Acessos</h3>
                            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1">
                                {alunos.find(a => a.id === showAccessModal)?.nome_completo}
                            </p>
                        </div>
                        <div className="p-6">
                            {authorizedPeople.length === 0 ? (
                                <div className="text-center py-8">
                                    <p className="text-gray-400 font-medium">Ninguém mais além de você tem acesso a este aluno.</p>
                                </div>
                            ) : (
                                <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2 scrollbar-hide">
                                    <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-2">Outras pessoas autorizadas:</p>
                                    {authorizedPeople.map((p) => (
                                        <div key={p.id} className="flex items-center justify-between p-3 rounded-2xl bg-gray-50 border border-gray-100">
                                            <div>
                                                <p className="font-bold text-gray-900 text-sm leading-tight">{p.usuario?.nome_completo}</p>
                                                <p className="text-[10px] text-gray-400 font-medium">{p.usuario?.telefone || 'Sem telefone'}</p>
                                            </div>
                                            <button
                                                onClick={() => handleRevokeAccess(p.id)}
                                                className="p-2 text-red-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                                                title="Revogar Acesso"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 000 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                                                </svg>
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <div className="mt-6 pt-4 border-t border-gray-100 italic text-[10px] text-gray-400 text-center">
                                Se você revogar o acesso, a pessoa não verá mais este aluno no App dela.
                            </div>
                            <Button
                                variant="outline"
                                onClick={() => setShowAccessModal(null)}
                                className="w-full mt-4 rounded-2xl py-3 border-gray-200 font-black text-xs uppercase"
                            >
                                Fechar
                            </Button>
                        </div>
                    </Card>
                </div>
            )}
        </div>
    );
}
